import type { Restaurant } from '@restaurant/api-client';
import type { UserContext } from '@restaurant/api-contracts';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

interface RestaurantState {
  userContext: UserContext | null;
  selectedFranchiseId: string | null;
  selectedRestaurantId: string | null;
  /** Full restaurant record fetched on app init and on restaurant switch. */
  restaurantConfig: Restaurant | null;
  /**
   * Restaurants this session tried to open and the API refused (403, or absent
   * from a freshly fetched /auth/me). Deliberately NOT persisted: it exists to
   * stop the gate ↔ picker ping-pong within a tab, and a reload should give a
   * revoked-then-restored account a clean try again.
   */
  deniedRestaurantIds: string[];
}

interface RestaurantActions {
  setUserContext: (ctx: UserContext) => void;
  selectRestaurant: (franchiseId: string, restaurantId: string) => void;
  setRestaurantConfig: (config: Restaurant) => void;
  /** Drop the current restaurant selection - sends the user back to the picker. */
  deselectRestaurant: () => void;
  /** Remember that the API refused this restaurant, so nothing re-selects it. */
  markRestaurantDenied: (restaurantId: string) => void;
  clear: () => void;
}

type RestaurantStore = RestaurantState & RestaurantActions;

const initialState: RestaurantState = {
  userContext: null,
  selectedFranchiseId: null,
  selectedRestaurantId: null,
  restaurantConfig: null,
  deniedRestaurantIds: [],
};

export const useRestaurantStore = create<RestaurantStore>()(
  persist(
    (set) => ({
      ...initialState,

      setUserContext: (ctx) => set({ userContext: ctx }),

      selectRestaurant: (franchiseId, restaurantId) =>
        set({
          selectedFranchiseId: franchiseId,
          selectedRestaurantId: restaurantId,
          restaurantConfig: null,
        }),

      setRestaurantConfig: (config) => set({ restaurantConfig: config }),

      deselectRestaurant: () =>
        set({ selectedFranchiseId: null, selectedRestaurantId: null, restaurantConfig: null }),

      markRestaurantDenied: (restaurantId) =>
        set((s) =>
          s.deniedRestaurantIds.includes(restaurantId)
            ? s
            : { deniedRestaurantIds: [...s.deniedRestaurantIds, restaurantId] },
        ),

      clear: () => set({ ...initialState }),
    }),
    {
      name: 'restaurant-context',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        selectedFranchiseId: state.selectedFranchiseId,
        selectedRestaurantId: state.selectedRestaurantId,
      }),
    },
  ),
);

/** IANA timezone for the currently selected restaurant. Falls back to UTC until config is loaded. */
export function useRestaurantTimezone(): string {
  return useRestaurantStore((s) => s.restaurantConfig?.timezone ?? 'UTC');
}
